import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import ErrorFallback from "components/ErrorFallBack/ErrorFallback";
import {
  DISCARD_ORDER_MODIFICATION_OPERATION,
  ORDER_MODIFICATION_SUMMARY_COLUMN,
} from "constants/config";
import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCancelOrderModification,
  useOrderModificationSummary,
} from "store/backend/assets";
import { PrimaryButton, Table } from "widgets";
import Loader from "Shared/Loader/Loader";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";

import styles from "./ViewOrderModificationSummary.module.scss";

const formatAddress = (addr) => {
  if (!addr) return "-";
  const parts = [
    addr.address_line_1,
    addr.address_line_2,
    addr.street,
    addr.city,
    addr.state,
    addr.postal_code,
    addr.country,
  ].filter(Boolean);
  return parts.length ? `${parts.join(", ")}.` : "-";
};

const formatPackages = (packages) => {
  if (!packages?.length) return "-";
  return packages.map((p) => p.displayName ?? p.name ?? "-").join("\n");
};

const getOrderModificationSummaryRows = (orderModificationSummary) => {
  if (!orderModificationSummary) return [];
  const current = orderModificationSummary.current_order;
  const proposed = orderModificationSummary.proposal_order;
  return [
    {
      description: "Course(s)",
      currentOrder: formatPackages(current?.packages),
      proposedOrder: formatPackages(proposed?.packages),
    },
    {
      description: "Order Amount",
      currentOrder: current.amount,
      proposedOrder: proposed.amount,
    },
    {
      description: "Billing Name",
      currentOrder: current?.billing_address?.recipient_name,
      proposedOrder: proposed?.billing_address?.recipient_name,
    },
    {
      description: "Billing Address",
      currentOrder: formatAddress(current?.billing_address),
      proposedOrder: formatAddress(proposed?.billing_address),
    },
    {
      description: "Shipping Name",
      currentOrder: current?.shipping_address?.recipient_name,
      proposedOrder: proposed?.shipping_address?.recipient_name,
    },
    {
      description: "Shipping Address",
      currentOrder: formatAddress(current?.shipping_address),
      proposedOrder: formatAddress(proposed?.shipping_address),
    },
  ];
};

const handleOnClickDiscardOrderModificationRequest = (
  orderId,
  cancelOrderModification,
  proposalId,
  openSnackbar,
  onClose,
  queryClient,
) => {
  const payload = {
    order_id: orderId,
    proposal_id: proposalId,
    operation: DISCARD_ORDER_MODIFICATION_OPERATION,
  };
  cancelOrderModification(payload, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["selected_order_id", orderId],
      });
      queryClient.invalidateQueries({
        queryKey: ["customer_order_details"],
      });
      queryClient.invalidateQueries({
        queryKey: ["orderModificationSummary", orderId],
      });
      onClose();
      openSnackbar("Order modification discarded successfully.", "success");
    },
    onError: (error) => {
      openSnackbar(
        `Error canceling the order modification: ${error.message}`,
        "error",
      );
    },
  });
};

const ViewOrderModificationSummary = (props) => {
  const { orderId, onClose } = props;
  const {
    data: orderModificationSummary,
    isLoading: isOrderModificationSummaryLoading,
    isError: isErrorOnOrderModificationSummary,
    error: errorMessageOnOrderModificationSummary,
  } = useOrderModificationSummary(orderId);
  const {
    mutate: cancelOrderModification,
    isPending: isCancelOrderModificationPending,
    isError: isErrorOnCancelOrderModification,
    error: errorMessageOnCancelOrderModification,
  } = useCancelOrderModification();
  const { openSnackbar } = useSnackbarContext();
  const queryClient = useQueryClient();

  if (isOrderModificationSummaryLoading) {
    return (
      <div className={styles.loaderWrapper}>
        <Loader />
      </div>
    );
  }
  if (isErrorOnOrderModificationSummary)
    return (
      <ErrorFallback message={errorMessageOnOrderModificationSummary.message} />
    );
  const rows = getOrderModificationSummaryRows(orderModificationSummary);

  const proposalId = orderModificationSummary.proposal_order.id;

  return (
    <div className={styles.cancelOrderModificationContainer}>
      <div className={styles.titleBar}>
        <div className={styles.header}>Order Modification Summary</div>
        <IconButton
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close dialog"
          size="small"
          edge="end"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
      <div className={styles.tableSection}>
        <Table columns={ORDER_MODIFICATION_SUMMARY_COLUMN} rows={rows} />
      </div>
      <div className={styles.buttonContainer}>
        <PrimaryButton
          onClick={() =>
            handleOnClickDiscardOrderModificationRequest(
              orderId,
              cancelOrderModification,
              proposalId,
              openSnackbar,
              onClose,
              queryClient,
            )
          }
          disabled={isCancelOrderModificationPending}
        >
          Discard Modification Request
        </PrimaryButton>
      </div>
    </div>
  );
};

export default ViewOrderModificationSummary;
